import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SetterOrUpdater, useRecoilValue } from 'recoil';
import styled, { css } from 'styled-components';
import {
  ButtonGroup as StyledButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { AccessAlarm, ArrowDropDownOutlined, Lock } from '@material-ui/icons';

import { Colors } from 'styles';
import { Button } from 'common/ui/Button';
import { Stack } from 'common/ui/Layout';
import Tooltip from 'common/ui/Tooltip';
import {
  ReportStatus,
  REPORT_EDITABLE_STATUSES,
  IN_FLIGHT_STATUSES,
  ReportStatuses,
  PROVISIONAL_SUBMIT_STATUSES,
  IN_FLIGHT_PRELIM_STATUSES,
} from 'domains/reporter/Reporter/state';
import getTimeRemaining from 'utils/getTimeRemaining';
import { useRecorder } from 'common/Recorder/useRecorder';
import { ReporterSaveState, reporterSavingState } from 'config/recoilState';
import { OnSubmitParams, SubmitType, VariantToStatusMapType } from '../types';
import { getButtonVariant } from './utils';

const FINAL_COUNTDOWN_TIME_SECONDS = 60;

const ButtonGroup = styled(StyledButtonGroup)`
  .dropdown-button {
    padding: 0;
  }
`;

export type GetButtonContentParams = {
  isRecording: boolean;
  isBypass: boolean;
};

export type ButtonLabels = {
  buttonTooltip: string;
  buttonGroupAriaLabel: string;
  buttonDataTestId: string;
  splitButtonDataTestId: string;
};

type ActionButtonProps = {
  countdownSeconds: number | null;
  status: ReportStatuses;
  submitType?: SubmitType;
  onSubmit: (({ type }: OnSubmitParams) => void) | (({ type }: OnSubmitParams) => Promise<void>);
  onCancelSubmit: (() => void) | (() => Promise<void>);
  getContent: (params: GetButtonContentParams) => React.ReactElement | string;
  variantByStatus?: VariantToStatusMapType;
  disabled: boolean;
  options?: string[];
  labels: ButtonLabels;
  isBypassEnabled?: boolean;
  setBypass: SetterOrUpdater<boolean>;
  isCountdownMode?: boolean;
};

function ActionButton({
  countdownSeconds,
  status,
  submitType = SubmitType.Final,
  onSubmit,
  onCancelSubmit,
  getContent,
  variantByStatus,
  disabled,
  options = [],
  labels,
  isBypassEnabled = false,
  setBypass,
  isCountdownMode,
}: ActionButtonProps): React.ReactElement {
  const [splitButtonOpen, setSplitButtonOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0); // 0 is Action, 1 is Bypass and Action
  const time = getTimeRemaining(countdownSeconds);
  const isInFinalCountdownState = (countdownSeconds ?? -1) <= FINAL_COUNTDOWN_TIME_SECONDS;
  const { isRecording } = useRecorder();
  const reportSaveState = useRecoilValue(reporterSavingState);
  const variant = getButtonVariant({ status, variantByStatus, isInFinalCountdownState });

  const isReportSaving = useMemo(() => {
    return reportSaveState === ReporterSaveState.Saving;
  }, [reportSaveState]);

  // Will disable the button if receives disabled prop, in-flight statuses matches the current status,
  // the nvoq queue of pending stable text is not empty or if actively recording
  const isRecordingProcessing =
    REPORT_EDITABLE_STATUSES.includes(status as (typeof REPORT_EDITABLE_STATUSES)[number]) &&
    isRecording;
  const isButtonDisabled = disabled || isRecordingProcessing || isReportSaving;

  const content = getContent({
    isRecording,
    isBypass: selectedIndex === 1,
  });

  const handleMenuItemClick = (event: React.MouseEvent, index: number) => {
    setSelectedIndex(index);
    setSplitButtonOpen(false);
  };

  const handleToggle = () => {
    setSplitButtonOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent> | MouseEvent) => {
    if (event.target instanceof Node && anchorRef.current?.contains(event.target)) {
      return;
    }
    setSplitButtonOpen(false);
  };

  const handleClick = () => {
    if (
      PROVISIONAL_SUBMIT_STATUSES.includes(status as (typeof PROVISIONAL_SUBMIT_STATUSES)[number])
    ) {
      onCancelSubmit();
    } else {
      onSubmit({ type: submitType });
    }
  };

  useEffect(() => {
    setBypass(isBypassEnabled && selectedIndex === 1);
  }, [isBypassEnabled, selectedIndex, setBypass]);

  return (
    <Stack
      stretchX
      stretchY
      alignY="center"
      css={css`
        padding: 4px 0px;
        width: 100%;
      `}
    >
      {isCountdownMode && (
        <Stack
          stretchY
          css={`
            position: relative;
            background-color: ${isInFinalCountdownState ? Colors.gray10 : Colors.yellow6};
            color: ${Colors.gray1};
            display: flex;
            align-items: center;
            border-radius: 2.5rem;
            right: -5rem;
            width: 13rem;
            padding-left: 1rem;
            height: 100%;
            padding: 0.36rem;
          `}
        >
          <span data-testid="countdown-icon">
            <AccessAlarm
              css={`
                color: ${isInFinalCountdownState ? Colors.red2 : 'inherit'};
                margin-right: 0.5rem;
              `}
            />
          </span>
          {time}
        </Stack>
      )}
      <Tooltip
        backgroundColor={Colors.gray7}
        color={Colors.gray1}
        content={isReportSaving ? labels.buttonTooltip : null}
      >
        <ButtonGroup variant="contained" ref={anchorRef} aria-label={labels.buttonGroupAriaLabel}>
          <Button
            variant={variant}
            disabled={isButtonDisabled}
            icon={status === ReportStatus.Submitted ? <Lock /> : null}
            onClick={handleClick}
            data-status={status}
            data-testid={labels.buttonDataTestId}
            css={`
              white-space: nowrap;
              padding: 4px ${isCountdownMode ? '2.5rem' : '24px'};
            `}
          >
            {content}
          </Button>
          {isBypassEnabled &&
            REPORT_EDITABLE_STATUSES.includes(
              status as (typeof REPORT_EDITABLE_STATUSES)[number]
            ) && (
              <Button
                data-testid={labels.splitButtonDataTestId}
                className="dropdown-button"
                variant={variant}
                disabled={isButtonDisabled}
                aria-controls={splitButtonOpen ? 'split-button-menu' : undefined}
                aria-expanded={splitButtonOpen ? 'true' : undefined}
                aria-haspopup="menu"
                onClick={() => {
                  !isButtonDisabled && handleToggle();
                }}
              >
                <ArrowDropDownOutlined />
              </Button>
            )}
        </ButtonGroup>
      </Tooltip>
      {((submitType === SubmitType.Prelim &&
        IN_FLIGHT_PRELIM_STATUSES.includes(status as (typeof IN_FLIGHT_PRELIM_STATUSES)[number])) ||
        (submitType === SubmitType.Final &&
          IN_FLIGHT_STATUSES.includes(status as (typeof IN_FLIGHT_STATUSES)[number]))) && (
        <CircularProgress style={{ marginLeft: '8px' }} size={16} />
      )}
      {options?.length > 0 && (
        <Popper open={splitButtonOpen} anchorEl={anchorRef.current} transition disablePortal>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem>
                    {options.map((option, index) => (
                      <MenuItem
                        key={option}
                        selected={index === selectedIndex}
                        onClick={(event) => handleMenuItemClick(event, index)}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </Stack>
  );
}

export { ActionButton };
